import {useState} from "react";
import {
    Modal, ModalBody, ModalHeader, ModalFooter,
    Button, Form, FormGroup, Label, Input, Table
} from "reactstrap";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {toast} from "react-toastify";
import {createCreditNote} from "../../api/api";
import Loader from "../../components/Loader";

function IssueCreditNoteModal({isOpen, toggle, order, invoiceId}) {
    const [reason, setReason] = useState("");
    const [creditNoteNotes, setCreditNoteNotes] = useState("");
    const [selectedItems, setSelectedItems] = useState({});
    const queryClient = useQueryClient();

    const resetForm = () => {
        setReason("");
        setCreditNoteNotes("");
        setSelectedItems({});
    };

    const handleToggle = () => {
        resetForm();
        toggle();
    };

    const handleItemToggle = (lineItemId) => {
        setSelectedItems(prev => {
            const updated = {...prev};
            if (updated[lineItemId]) {
                delete updated[lineItemId];
            } else {
                const item = order.order_items.find(i => i.id === lineItemId);
                updated[lineItemId] = {
                    order_line_item: lineItemId,
                    quantity: item?.quantity || 1,
                    maxQuantity: item?.quantity || 1,
                };
            }
            return updated;
        });
    };

    const handleQuantityChange = (lineItemId, value) => {
        const qty = parseInt(value, 10);
        if (isNaN(qty) || qty < 1) return;
        setSelectedItems(prev => ({
            ...prev,
            [lineItemId]: {
                ...prev[lineItemId],
                quantity: Math.min(qty, prev[lineItemId].maxQuantity),
            }
        }));
    };

    const createCreditNoteMutation = useMutation({
        mutationFn: createCreditNote,
        onSuccess: () => {
            queryClient.invalidateQueries(['creditNotes']);
            queryClient.invalidateQueries(['orders']);
            toast.success("Credit Note created successfully!");
            handleToggle();
        },
        onError: (err) => {
            const detail = err?.response?.data?.detail
                || err?.response?.data?.non_field_errors?.[0]
                || JSON.stringify(err?.response?.data)
                || "An error occurred";
            toast.error(`Failed to create credit note: ${detail}`);
        }
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!reason.trim()) {
            toast.error("Please provide a reason for the credit note.");
            return;
        }
        if (Object.keys(selectedItems).length === 0) {
            toast.error("Please select at least one line item.");
            return;
        }

        const lineItems = Object.values(selectedItems).map(item => ({
            order_line_item: item.order_line_item,
            quantity: item.quantity,
        }));

        createCreditNoteMutation.mutate({
            invoice: invoiceId,
            reason: reason,
            credit_note_notes: creditNoteNotes,
            line_items: lineItems,
        });
    };

    const selectedCount = Object.keys(selectedItems).length;

    return (
        <Modal className="modal-lg" isOpen={isOpen} toggle={handleToggle}>
            <ModalHeader toggle={handleToggle} close={
                <button className="close" onClick={handleToggle}>&times;</button>
            }>
                Issue Partial Credit Note
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    <p className="text-muted mb-3">
                        Select the items and quantities to include in the credit note.
                    </p>

                    <FormGroup>
                        <Label for="cn-reason">Reason <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="cn-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Item returned, Price adjustment, Defective item"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="cn-notes">Notes</Label>
                        <Input
                            type="textarea"
                            id="cn-notes"
                            value={creditNoteNotes}
                            onChange={(e) => setCreditNoteNotes(e.target.value)}
                            placeholder="Additional notes (optional)"
                            rows={2}
                        />
                    </FormGroup>

                    <Label>Order Items</Label>
                    <Table bordered size="sm" responsive>
                        <thead className="bg-light">
                        <tr>
                            <th style={{width: '40px'}}></th>
                            <th>Item Name</th>
                            <th>Type</th>
                            <th>Rate</th>
                            <th>Order Qty</th>
                            <th>Credit Qty</th>
                            <th>Tax %</th>
                            <th>Total</th>
                        </tr>
                        </thead>
                        <tbody>
                        {order.order_items.map((item) => {
                            const isSelected = !!selectedItems[item.id];
                            const creditQty = selectedItems[item.id]?.quantity || 0;
                            const perUnitTotal = item.total_amount / item.quantity;
                            const creditTotal = isSelected ? (perUnitTotal * creditQty).toFixed(2) : "0.00";

                            return (
                                <tr key={item.id} className={isSelected ? "table-active" : ""}>
                                    <td className="text-center align-middle">
                                        <Input
                                            type="checkbox"
                                            style={{ margin: 0, display: 'block' }}
                                            checked={isSelected}
                                            onChange={() => handleItemToggle(item.id)}
                                        />
                                    </td>
                                    <td>{item.name}</td>
                                    <td>{item.type}</td>
                                    <td>{item.rate}</td>
                                    <td>{item.quantity}</td>
                                    <td style={{width: '90px'}}>
                                        {isSelected ? (
                                            <Input
                                                type="number"
                                                min={1}
                                                max={item.quantity}
                                                value={selectedItems[item.id]?.quantity}
                                                onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                bsSize="sm"
                                            />
                                        ) : "-"}
                                    </td>
                                    <td>{item.tax_percentage}%</td>
                                    <td>{isSelected ? `${creditTotal}` : "-"}</td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </Table>

                    {selectedCount > 0 && (
                        <div className="text-end mt-2">
                            <strong>Selected Items: </strong>{selectedCount} &nbsp;|&nbsp;
                            <strong>Estimated Credit Total: </strong>
                            {Object.entries(selectedItems).reduce((total, [itemId, sel]) => {
                                const item = order.order_items.find(i => i.id === itemId);
                                if (!item) return total;
                                const perUnit = item.total_amount / item.quantity;
                                return total + (perUnit * sel.quantity);
                            }, 0).toFixed(2)}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={handleToggle}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        type="submit"
                        disabled={createCreditNoteMutation.isLoading || selectedCount === 0 || !reason.trim()}
                    >
                        {createCreditNoteMutation.isLoading
                            ? <><Loader size={'small'}/> Creating...</>
                            : `Issue Credit Note`
                        }
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
}

export default IssueCreditNoteModal;
